import { FormattedMessage } from 'react-intl';

import { StringHelperService } from '../../../../services/string-helper-service';

import { OrderNumberReturnsGridColumn } from './column-components/order-number';
import { ReturnsRequestNumberColumn } from './column-components/request-number';
import { StoreReturnsGridColumn } from './column-components/store';
import { TrackingNumberColumn as TrackingNumberComponent } from './column-components/tracking-number';
import { CarrierServiceColumn as CarrierServiceComponent } from './column-components/carrier-service';
import FormattedCurrency from '../../../../components/formatted-currency';
import UserDateTimezoneFormat from '../../../../components/user-date-timezone-format';
import { columnMessages } from '../messages';
import { StatusColumnComponent } from './column-components/status';

export const REQUEST_NUMBER_COLUMN_ID = 'RequestNumber';
export const ORDER_NUMBER_COLUMN_ID = 'OrderNumber';
export const CREATE_DATE_COLUMN_ID = 'CreateDateTime';
export const CUSTOMER_NAME_COLUMN_ID = 'CustomerName';
export const CUSTOMER_EMAIL_COLUMN_ID = 'CustomerEmail';
export const STATUS_COLUMN_ID = 'Status';
export const LOCKED_REASON_COLUMN_ID = 'LockedReason';
export const AMOUNT_COLUMN_ID = 'Amount';
export const STORE_COLUMN_ID = 'Store';
export const TRACKING_NUMBER_COLUMN_ID = 'TrackingNumber';
export const CARRIER_SERVICE_COLUMN_ID = 'CarrierService';
export const SOURCE_COLUMN_ID = 'Source';

export const RequestNumberColumn = {
  name: <FormattedMessage {...columnMessages.requestNumber} />,
  getMessageDescriptor: () => columnMessages.requestNumber,
  id: REQUEST_NUMBER_COLUMN_ID,
  render: (data) => {
    return <ReturnsRequestNumberColumn id={data.id} returnNumber={data.name} salesOrderId={data.salesOrderGuid} />;
  },
};

export const OrderNumberColumn = {
  name: <FormattedMessage {...columnMessages.orderNumber} />,
  getMessageDescriptor: () => columnMessages.orderNumber,
  id: ORDER_NUMBER_COLUMN_ID,
  render: (data) => {
    return <OrderNumberReturnsGridColumn id={data.id} orderNumber={data.orderName} salesOrderId={data.salesOrderGuid} />;
  },
};

export const CreateDateColumn = {
  name: <FormattedMessage {...columnMessages.createDate} />,
  getMessageDescriptor: () => columnMessages.createDate,
  id: CREATE_DATE_COLUMN_ID,
  render: (data) => {
    // TODO: remove modifiedOn once createDateTime lands in backend in master
    const date = data.createDateTime || data.modifiedOn;
    if (date) {
      return <UserDateTimezoneFormat dateTime={date} />;
    }

    return '';
  },
};

export const CustomerNameColumn = {
  name: <FormattedMessage {...columnMessages.customerName} />,
  getMessageDescriptor: () => columnMessages.customerName,
  id: CUSTOMER_NAME_COLUMN_ID,
  render: (data) => {
    return data.customerName || '';
  },
};

export const CustomerEmailColumn = {
  name: <FormattedMessage {...columnMessages.customerEmail} />,
  getMessageDescriptor: () => columnMessages.customerEmail,
  id: CUSTOMER_EMAIL_COLUMN_ID,
  render: (data) => {
    return data.customerEmail || '';
  },
};

export const StatusColumn = {
  name: <FormattedMessage {...columnMessages.status} />,
  getMessageDescriptor: () => columnMessages.status,
  id: STATUS_COLUMN_ID,
  render: (data) => {
    return <StatusColumnComponent status={data.status} />;
  },
};

export const LockedReasonColumn = {
  name: <FormattedMessage {...columnMessages.lockedReason} />,
  getMessageDescriptor: () => columnMessages.lockedReason,
  id: LOCKED_REASON_COLUMN_ID,
  render: (data) => {
    return data.onHoldReason || '';
  },
};

export const StoreIconColumn = {
  name: <FormattedMessage {...columnMessages.store} />,
  getMessageDescriptor: () => columnMessages.store,
  id: STORE_COLUMN_ID,
  render: (data) => <StoreReturnsGridColumn salesOrderGuid={data.salesOrderGuid} />,
};

// TODO: we might need the backend to map currency code if they are not ISO standard from SSR
export const AmountColumn = {
  name: <FormattedMessage {...columnMessages.amount} />,
  getMessageDescriptor: () => columnMessages.amount,
  id: AMOUNT_COLUMN_ID,
  render: (data) => {
    return <FormattedCurrency currencyCode={data.currency} value={data.amount} />;
  },
};

export const TrackingNumberColumn = {
  name: <FormattedMessage {...columnMessages.trackingNumber} />,
  getMessageDescriptor: () => columnMessages.trackingNumber,
  id: TRACKING_NUMBER_COLUMN_ID,
  render: (data) => {
    return <TrackingNumberComponent trackingNumber={data.trackingNumber} trackingUrl={data.trackingUrl} />;
  },
};

export const CarrierServiceColumn = {
  name: <FormattedMessage {...columnMessages.carrierService} />,
  getMessageDescriptor: () => columnMessages.carrierService,
  id: CARRIER_SERVICE_COLUMN_ID,
  render: (data) => {
    return <CarrierServiceComponent carrierId={data.carrierId} trackingService={data.trackingService} />;
  },
};

export const SourceColumn = {
  name: <FormattedMessage {...columnMessages.source} />,
  getMessageDescriptor: () => columnMessages.source,
  id: SOURCE_COLUMN_ID,
  render: (data) => StringHelperService.humanize(data.importedFrom),
};

// TODO: settle on width defaults with Design https://auctane.atlassian.net/browse/SPD-20142
export const baseColumnsDefaultConfigs = [
  {
    id: REQUEST_NUMBER_COLUMN_ID,
    minWidth: 225,
    width: 225,
    nonsortable: true,
  },
  {
    id: STORE_COLUMN_ID,
    minWidth: 200,
    width: 200,
    nonsortable: true,
  },
  {
    id: CREATE_DATE_COLUMN_ID,
    minWidth: 125,
    width: 125,
    sorted: 'descending',
  },
  {
    id: ORDER_NUMBER_COLUMN_ID,
    minWidth: 150,
    width: 150,
    nonsortable: true,
  },
  {
    id: SOURCE_COLUMN_ID,
    minWidth: 150,
    width: 150,
    nonsortable: true,
  },
  {
    id: CUSTOMER_NAME_COLUMN_ID,
    minWidth: 200,
    width: 200,
    nonsortable: true,
  },
  {
    id: CUSTOMER_EMAIL_COLUMN_ID,
    minWidth: 300,
    width: 300,
    nonsortable: true,
  },
  {
    id: TRACKING_NUMBER_COLUMN_ID,
    minWidth: 175,
    width: 175,
    nonsortable: true,
  },
  {
    id: CARRIER_SERVICE_COLUMN_ID,
    minWidth: 200,
    width: 200,
    nonsortable: true,
  },
  {
    id: AMOUNT_COLUMN_ID,
    minWidth: 125,
    width: 125,
    nonsortable: true,
  },
  {
    id: STATUS_COLUMN_ID,
    minWidth: 150,
    width: 150,
    nonsortable: true,
  },
  {
    id: LOCKED_REASON_COLUMN_ID,
    minWidth: 150,
    width: 150,
    nonsortable: true,
  },
].map((c, index) => ({ ...c, index }));

export const allColumns = [
  RequestNumberColumn,
  CreateDateColumn,
  OrderNumberColumn,
  SourceColumn,
  CustomerNameColumn,
  CustomerEmailColumn,
  TrackingNumberColumn,
  CarrierServiceColumn,
  AmountColumn,
  StatusColumn,
  LockedReasonColumn,
  StoreIconColumn,
];
